import React from 'react';
import { getPmsToolbarIcon } from '../shared/pmsToolbarIcon';
import { createPmsGetTableActions } from '../shared/pmsTableActions';
import {
  pmsEquipmentField,
  pmsEquipmentInstanceStatusField,
  pmsPreviousInstanceField} from '../shared/pmsFormFields';
import { Column } from '../../../components/DataTable/DataTable';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import type { PmsEquipmentInstance } from './useData';

export const moduleConfig = {
  module: {
    title: "Equipment instances",
    permissionModuleKey: 'pms_hierarchy',
    route: {
      list: '/pms/pms-equipment-instances',
      new: '/pms/pms-equipment-instances/new',
      detail: (id: number) => `/pms/pms-equipment-instances/${id}`,
      edit: (id: number) => `/pms/pms-equipment-instances/${id}/edit`},
    api: { list: '/pms-equipment-instances', detail: (id: number) => `/pms-equipment-instances/${id}` }},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search…' },
  filterGroups: [],
  filters: { fields: [], applyFilter: (items: PmsEquipmentInstance[]) => items ,
  },
  toolbar: {
    actions: [{ key: 'add', icon: 'add', title: 'New', showTitle: false, variant: 'primary' as const, href: '/pms/pms-equipment-instances/new' }]},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: PmsEquipmentInstance) => item.id,
  },
  emptyStates: { noData: 'No records.', noSearchResults: 'No matches.', noFilterResults: 'No matches.' },
  delete: {
    title: 'Delete',
    message: 'Delete this record?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete.',
    handler: async (id: number) => {
      const { deletePmsEquipmentInstance } = await import('../../../services/mutations');
      await deletePmsEquipmentInstance(id);
    }},
  utils: { formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '') },
  detail: {
    subtitle: 'Details',
    notFoundTitle: 'Not found',
    notFoundMessage: 'Not found.',
    notFoundBackText: 'Back',
    defaultTitle: "Equipment instances",
    getTitle: (item: PmsEquipmentInstance) =>
      item.serialNumber
        ? item.serialNumber
        : item.equipment?.name
          ? `${item.equipment.name} — #${item.id}`
          : `#${item.id}`},
  form: {
    new: { title: 'New', submitText: 'Create' },
    edit: { title: 'Edit', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.',
    create: async (data: Partial<PmsEquipmentInstance>) => {
      const { createPmsEquipmentInstance } = await import('../../../services/mutations');
      return createPmsEquipmentInstance(data as Record<string, unknown>);
    },
    update: async (id: number, data: Partial<PmsEquipmentInstance>) => {
      const { updatePmsEquipmentInstance } = await import('../../../services/mutations');
      return updatePmsEquipmentInstance(id, data as Record<string, unknown>);
    }},
  fields: [
    pmsEquipmentField(true),
    pmsEquipmentInstanceStatusField(true),
    { name: 'serialNumber', label: 'Serial number', type: 'text' },
    { name: 'manufacturer', label: 'Manufacturer', type: 'text' },
    { name: 'model', label: 'Model', type: 'text' },
    { name: 'partNumber', label: 'Part number', type: 'text' },
    { name: 'installedLocation', label: 'Installed location', type: 'text' },
    { name: 'condition', label: 'Condition', type: 'text' },
    pmsPreviousInstanceField(false),
    { name: 'notes', label: 'Notes', type: 'textarea', rows: 3 },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'placement',
        title: 'Equipment & status',
        icon: '',
        fields: ['equipmentId', 'statusId', 'previousInstanceId'],
        collapsible: false},
      {
        id: 'main',
        title: 'Instance details',
        icon: '',
        fields: [
          'serialNumber',
          'manufacturer',
          'model',
          'partNumber',
          'installedLocation',
          'condition',
          'notes',
        ],
        collapsible: false},
    ]} as FormLayout};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

const labelEquipment = (item: PmsEquipmentInstance) => {
  return item.equipment?.name ?? (item.equipmentId != null ? `#${item.equipmentId}` : '-');
};

const labelStatus = (item: PmsEquipmentInstance) => {
  if (item.status?.name) return item.status.name;
  return item.status?.code ?? (item.statusId != null ? `#${item.statusId}` : '-');
};

const labelPrevious = (item: PmsEquipmentInstance) => {
  if (item.previousInstance?.serialNumber) return item.previousInstance.serialNumber;
  return item.previousInstanceId != null ? `#${item.previousInstanceId}` : '-';
};

export const getColumns = (_formatDate: (dateString?: string) => string): Column<PmsEquipmentInstance>[] => [
  { key: 'serialNumber', header: 'Serial', sortable: true, render: (v) => <TextCell value={v != null ? String(v) : undefined} /> },
  {
    key: 'equipmentId',
    header: 'Equipment',
    sortable: true,
    render: (_v, row) => <TextCell value={labelEquipment(row)} />},
  {
    key: 'statusId',
    header: 'Status',
    sortable: true,
    render: (_v, row) => <TextCell value={labelStatus(row)} />},
];

export const getDetailSections = (item: PmsEquipmentInstance, formatDate: (s?: string) => string) => [
  {
    title: 'Overview',
    fields: [
      { label: 'Equipment', value: labelEquipment(item) },
      { label: 'Status', value: labelStatus(item) },
      { label: 'Serial number', value: item.serialNumber != null ? String(item.serialNumber) : '' },
      { label: 'Manufacturer', value: item.manufacturer != null ? String(item.manufacturer) : '-' },
      { label: 'Model', value: item.model != null ? String(item.model) : '-' },
      { label: 'Part number', value: item.partNumber != null ? String(item.partNumber) : '-' },
      { label: 'Installed location', value: item.installedLocation != null ? String(item.installedLocation) : '-' },
      { label: 'Condition', value: item.condition != null ? String(item.condition) : '-' },
      { label: 'Previous instance', value: labelPrevious(item) },
      { label: 'Notes', value: item.notes != null ? String(item.notes) : '-' },
      { label: 'Created', value: formatDate(item.createdAt) },
    ]},
];

export const getStatusBadges = () => null;
export const getInitialFormData = (): Partial<PmsEquipmentInstance> => ({
  equipmentId: undefined,
  statusId: undefined,
  serialNumber: '',
  manufacturer: '',
  model: '',
  partNumber: '',
  installedLocation: '',
  condition: '',
  notes: '',
  previousInstanceId: undefined});
export const mapItemToFormData = (item: PmsEquipmentInstance): Partial<PmsEquipmentInstance> => ({
  equipmentId: item.equipmentId != null ? Number(item.equipmentId) : undefined,
  statusId: item.statusId != null ? Number(item.statusId) : undefined,
  serialNumber: item.serialNumber ?? '',
  manufacturer: item.manufacturer ?? '',
  model: item.model ?? '',
  partNumber: item.partNumber ?? '',
  installedLocation: item.installedLocation ?? '',
  condition: item.condition ?? '',
  notes: item.notes ?? '',
  previousInstanceId:
    item.previousInstanceId != null ? Number(item.previousInstanceId) : undefined});
export const getToolbarIcon = getPmsToolbarIcon;
export const getTableActions = createPmsGetTableActions(moduleConfig.module.route);
